// Shared configuration for the JS/TS apps. Environment and Cognito pool are driven at build time by
// env vars; the rationale for each value is in docs/sviluppo/environment-staging-vs-prod.md.

/** `staging | prod`, matching services/api's VOLEE_ENV. Both exist since 2026-09-29 — see
 * docs/sviluppo/environment-staging-vs-prod.md. */
export type Environment = 'staging' | 'prod'

/**
 * The `/volee/<env>/api/base-url` SSM parameter, baked in. Only a hand-built bundle reads it.
 */
export const BASE_URLS: Record<Environment, string> = {
  staging: 'https://gou20zuym7.execute-api.eu-central-1.amazonaws.com', // /volee/staging/api/base-url
  // Empty because CI bakes the deployed URL from /volee/prod/api/base-url via
  // VITE_API_BASE_URL at build time, never because prod is missing (docs/infra.md).
  prod: '',
}

/** `prod` must be an explicit opt-in, never derived from `import.meta.env.MODE`. */
function resolveEnv(): Environment {
  const raw = import.meta.env?.VITE_VOLEE_ENV
  return raw === 'prod' || raw === 'staging' ? raw : 'staging'
}

export const ENV: Environment = resolveEnv()

/** Where `services/api` serves locally (`uvicorn ... --port 8000`). */
const LOCAL_API_URL = 'http://localhost:8000'

/**
 * A production build with no URL fails at first import, rather than fetching the SPA fallback.
 */
function resolveApiBaseUrl(): string {
  const override = import.meta.env?.VITE_API_BASE_URL
  if (override) return override
  const baked = BASE_URLS[ENV]
  if (baked) return baked
  if (import.meta.env?.DEV) return LOCAL_API_URL
  throw new Error(
    `The API base URL for "${ENV}" is empty: set VITE_API_BASE_URL at build time ` +
      '(CI reads it from SSM /volee/<env>/api/base-url) or fill BASE_URLS in @volee/config.',
  )
}

export const API_BASE_URL = resolveApiBaseUrl()

// The `volee-staging` pool created by infra/, not the PoC's hand-made `volee-demo`.
// Public client-side values, not secrets; CI reads the live ones from SSM at deploy time.
export const COGNITO_REGION = import.meta.env?.VITE_COGNITO_REGION || 'eu-central-1'
export const COGNITO_USER_POOL_ID =
  import.meta.env?.VITE_COGNITO_USER_POOL_ID || 'eu-central-1_YBcVkAXwx'
export const COGNITO_CLIENT_ID =
  import.meta.env?.VITE_COGNITO_CLIENT_ID || '15ripa1v9qiuatfj419f1b13e7'

/** Cognito IDP JSON endpoint (InitiateAuth, ChangePassword, ForgotPassword, ...). */
export const COGNITO_IDP_ENDPOINT = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`

/** OIDC authority (issuer) = IDP endpoint + user pool. */
export const COGNITO_AUTHORITY = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/${COGNITO_USER_POOL_ID}`

// Mirrors `password_policy` in infra/modules/cognito/main.tf, which stays the authority:
// this copy feeds the hint under the field and a `minLength` attribute, never a validator.
export const PASSWORD_POLICY = {
  minLength: 8,
  requireLowercase: true,
  requireUppercase: true,
  requireNumbers: true,
  requireSymbols: false,
} as const

/** The domain of the internal mail behind a child's username, under `.invalid` so nothing is ever
 *  delivered there: the twin of INTERNAL_MAIL_DOMAIN in services/api/src/volee_api/core/usernames.py. */
export const INTERNAL_MAIL_DOMAIN = 'utenti.volee.invalid'

// Empty means billing is not configured here: the checkout says so instead of crashing.
// CI maps infra's "none" sentinel to empty before the build (.gitlab-ci.yml).
export const STRIPE_PUBLISHABLE_KEY: string = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY ?? ''
